package br.com.texeratx.boardgametimeapp.domain.usecases.player

import br.com.texeratx.boardgametimeapp.data.model.player.PlayerDetailDTO
import br.com.texeratx.boardgametimeapp.data.repository.PlayerRepository
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.toUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.PlayerDetailStatsUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.PlayerDetailUIModel
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.PlayerGameWinUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.PlayerMatchupStatsUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.PlayerOwnedGameUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.MatchupPeriodUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.MatchupHighlightsUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.RivalMatchupUI
import br.com.texeratx.boardgametimeapp.ui.screens.players.detail.MatchupGameSummaryUI

class GetPlayerDetailsUseCase(
    private val repository: PlayerRepository
) {
    suspend operator fun invoke(playerId: Long): Result<PlayerDetailUIModel> {
        return repository.getPlayerDetailById(playerId).map { dto ->
            dto.toPlayerDetailUIModel()
        }
    }
}

internal fun PlayerDetailDTO.toPlayerDetailUIModel(): PlayerDetailUIModel {
    return PlayerDetailUIModel(
        id = id,
        name = name,
        imageUrl = imageUrl,
        nickname = nickname,
        birthday = birthday,
        phone = phone,
        clanId = clanId,
        gamesOwned = gamesOwned.map { game ->
            PlayerOwnedGameUI(
                id = game.id,
                name = game.name,
                imageUrl = game.imageUrl
            )
        },
        recentMatches = recentMatches.map { it.toUI() },
        winsByGame = winsByGame.map { game ->
            PlayerGameWinUI(
                gameId = game.gameId,
                gameName = game.gameName,
                gameImageUrl = game.gameImageUrl,
                wins = game.wins
            )
        },
        stats = PlayerDetailStatsUI(
            wins = stats.wins,
            participations = stats.participations,
            minutesPlayed = stats.minutesPlayed,
            winsCurrentYear = stats.winsCurrentYear,
            participationsCurrentYear = stats.participationsCurrentYear,
            minutesPlayedCurrentYear = stats.minutesPlayedCurrentYear
        ),
        matchupStats = matchupStats?.let { dto ->
            PlayerMatchupStatsUI(
                total = dto.total.toUI(),
                currentYear = dto.currentYear.toUI()
            )
        }
    )
}

private fun br.com.texeratx.boardgametimeapp.data.model.player.MatchupPeriodDTO.toUI(): MatchupPeriodUI {
    return MatchupPeriodUI(
        highlights = MatchupHighlightsUI(
            mostDefeated = highlights.mostDefeated?.toUI(),
            mostLostTo = highlights.mostLostTo?.toUI(),
            mostPlayedTogether = highlights.mostPlayedTogether?.toUI()
        ),
        topRivals = topRivals.map { it.toUI() }
    )
}

private fun br.com.texeratx.boardgametimeapp.data.model.player.RivalMatchupDTO.toUI(): RivalMatchupUI {
    return RivalMatchupUI(
        opponentId = opponentId,
        opponentName = opponentName,
        opponentNickname = opponentNickname,
        opponentImageUrl = opponentImageUrl,
        matchesTogether = matchesTogether,
        wins = wins,
        losses = losses,
        winRatePct = winRatePct,
        dominance = dominance,
        lastMatchDate = lastMatchDate,
        bestGameAgainst = bestGameAgainst?.let { game ->
            MatchupGameSummaryUI(
                gameId = game.gameId,
                gameName = game.gameName,
                gameImageUrl = game.gameImageUrl,
                wins = game.wins,
                losses = game.losses
            )
        },
        worstGameAgainst = worstGameAgainst?.let { game ->
            MatchupGameSummaryUI(
                gameId = game.gameId,
                gameName = game.gameName,
                gameImageUrl = game.gameImageUrl,
                wins = game.wins,
                losses = game.losses
            )
        }
    )
}
